import { Component, inject, input, output, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { Organization } from '../../../organization-models';
import { PriceRegulation, PriceRegulationSearchEvent } from 'src/app/features/prices/price-regulations/price-regulation';
import { PriceRegulationService } from 'src/app/shared/services/price-regulation.service';
import { take } from 'rxjs';
import { SnackbarService } from 'ok-angular-shared/components';
import { PageEvent } from '@angular/material/paginator';
import { Step4PriceRegulationsListComponent } from './price-regulations-list/price-regulations-list.component';
import { Step4PriceRegulationsCreateComponent } from './price-regulation-create/price-regulation-create.component';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from 'src/app/shared/confirm-dialog/confirm-dialog.component';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';

@Component({
  selector: 'adm-step4-price-regulations',
  templateUrl: './step4-price-regulations.component.html',
  styleUrl: './step4-price-regulations.component.scss',
  imports: [MatButtonModule, Step4PriceRegulationsListComponent, Step4PriceRegulationsCreateComponent],
})
export class Step4PriceRegulationsComponent {
  private readonly deletePriceRegulationHasLocationCode = 906003;

  private priceRegulationService = inject(PriceRegulationService);
  private snackbarService = inject(SnackbarService);
  private dialog = inject(MatDialog);

  organization = input.required<Organization>();
  skipStep = output<void>();

  public organizationRegulations = signal<PriceRegulation[]>([]);
  public regulationToEdit = signal<PriceRegulation | null>(null);

  public onRegulationCreated() {
    this.regulationToEdit.set(null);
    this.loadOrganizationRegulations();
  }

  public openEditRegulation(regulation: PriceRegulation) {
    this.regulationToEdit.set(regulation);
  }

  public onRegulationEditClosed() {
    this.regulationToEdit.set(null);
  }

  public deleteRegulation(regulation: PriceRegulation) {
    const confirmDialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        icon: { name: 'trash-s' },
        title: 'Slet prisregulering',
        text: `Er du sikker på at du vil slette prisreguleringen <strong>${regulation.name}</strong>?`,
        confirmText: 'Ja',
        denyText: 'Nej',
      },
      disableClose: true,
    });

    confirmDialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe(confirmed => {
        if (!confirmed) {
          return;
        }

        this.priceRegulationService
          .deletePriceRegulation(regulation.identifier, '')
          .pipe(take(1))
          .subscribe({
            next: () => {
              if (this.regulationToEdit()?.identifier === regulation.identifier) {
                this.regulationToEdit.set(null);
              }
              this.loadOrganizationRegulations();
            },
            error: (err: HttpErrorResponse) => {
              if (err.status === HttpStatusCode.BadRequest && err.error?.code === this.deletePriceRegulationHasLocationCode) {
                this.snackbarService.error('Prisreguleringen har lokationer tilknyttet og kan derfor ikke slettes.');
                return;
              }

              if (err.status === HttpStatusCode.NotFound) {
                this.snackbarService.error('Prisreguleringen kunne ikke findes.');
                return;
              }

              this.snackbarService.error(`Der skete fejl under sletning (${err.error?.code ?? err.status})`);
            },
          });
      });
  }

  private loadOrganizationRegulations() {
    const paging = new PageEvent();
    paging.pageIndex = 0;
    paging.pageSize = 1000;

    const searchEvent = new PriceRegulationSearchEvent(paging);
    searchEvent.organizationId = this.organization().id;

    this.priceRegulationService
      .getPriceRegulations(searchEvent)
      .pipe(take(1))
      .subscribe({
        next: result => {
          this.organizationRegulations.set(result.items);
        },
        error: () => {
          this.snackbarService.error('Der skete en ukendt fejl ved hentning af prisreguleringer.');
        },
      });
  }
}
